import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check, Sparkles } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../../lib/AuthContext';
import { trackCreditPurchaseInitiated } from '../../utils/gtm';
import { supabase } from '../../lib/supabase-client';

interface PricingDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface Plan {
    id: string;
    name: string;
    price: number;
    credits: number;
    features: string[];
    checkout_url: string;
    // recommended field removed from DB schema, using simple check in render for now
}

// TEMPORARY: Disable payments while in test mode
// Set to false when ready for production payments
const PAYMENTS_TEMPORARILY_DISABLED = false;

export function PricingDialog({ open, onOpenChange }: PricingDialogProps) {
    const { user } = useAuth();
    const { subscription } = useSubscription();
    const [loading, setLoading] = useState<string | null>(null);
    const [plans, setPlans] = useState<Plan[]>([]);

    useEffect(() => {
        if (open) {
            const fetchPlans = async () => {
                try {
                    // Cast to any to bypass type check for new table
                    const { data, error } = await (supabase as any)
                        .from('plans')
                        .select('*')
                        .order('price', { ascending: true });

                    if (data) {
                        setPlans(data as Plan[]);
                    }
                    if (error) console.error('Error loading plans:', error);
                } catch (e) {
                    console.error('Error fetching plans', e);
                }
            };
            fetchPlans();
        }
    }, [open]);

    const currentPlan = subscription?.plan_type || 'free';

    const handleSelectPlan = (plan: Plan) => {
        const checkoutUrl = plan.checkout_url;

        if (!checkoutUrl) {
            console.warn('Checkout URL not configured for plan:', plan.id);
            // Redirect to Lemon Squeezy store page - user can contact for purchase
            window.open('https://wysko.lemonsqueezy.com/buy', '_blank');
            return;
        }

        // Track purchase initiation
        trackCreditPurchaseInitiated(plan.id, plan.price, plan.credits);

        setLoading(plan.id);

        // Build checkout URL with user data
        // Check if URL already has params
        const separator = checkoutUrl.includes('?') ? '&' : '?';
        const params = new URLSearchParams({
            'checkout[custom][user_id]': user?.id || '',
            'checkout[email]': user?.email || '',
        });

        window.open(`${checkoutUrl}${separator}${params.toString()}`, '_blank');
        setLoading(null);
        onOpenChange(false);
    };

    if (plans.length === 0) {
        // Return null or maybe a loading spinner if preferred, 
        // effectively hidden if no plans loaded yet.
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl sm:max-w-5xl w-full p-0 overflow-hidden">
                <DialogHeader className="p-6 pb-4">
                    <DialogTitle className="text-xl font-semibold">Wybierz plan</DialogTitle>
                    <DialogDescription>
                        Wybierz plan, który najlepiej odpowiada Twoim potrzebom.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 pt-2">
                    {plans.map((plan) => (
                        <div
                            key={plan.id}
                            className={`relative rounded-xl border p-5 flex flex-col ${plan.id === 'pro' // Simple logic for recommendation highlight
                                ? 'border-primary bg-primary/5 shadow-lg'
                                : 'border-border bg-card'
                                }`}
                        >
                            {/* Recommended Badge - assuming Pro is strictly recommended for now or needs DB flag */}
                            {plan.id === 'pro' && (
                                <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3">
                                    <Sparkles className="w-3 h-3 mr-1" />
                                    Polecany
                                </Badge>
                            )}

                            {/* Plan Header */}
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold">{plan.name}</h3>
                                <div className="mt-2 flex items-baseline gap-1">
                                    <span className="text-3xl font-bold">{plan.price}</span>
                                    <span className="text-muted-foreground text-sm">PLN/mies.</span>
                                </div>
                                <div className="mt-1 text-sm text-muted-foreground">
                                    {plan.credits} kredytów miesięcznie
                                </div>
                            </div>

                            {/* Features */}
                            <ul className="flex-1 space-y-2 mb-5">
                                {plan.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm">
                                        <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            {/* CTA Button */}
                            <Button
                                className={`w-full ${plan.id === 'pro' ? '' : 'variant-outline'}`}
                                variant={plan.id === 'pro' ? 'default' : 'outline'}
                                disabled={PAYMENTS_TEMPORARILY_DISABLED || currentPlan === plan.id || loading === plan.id}
                                onClick={() => handleSelectPlan(plan)}
                            >
                                {PAYMENTS_TEMPORARILY_DISABLED
                                    ? 'Wkrótce dostępne'
                                    : loading === plan.id
                                        ? 'Przekierowuję...'
                                        : currentPlan === plan.id
                                            ? 'Aktualny plan'
                                            : 'Wybierz plan'}
                            </Button>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="border-t border-border p-4 text-center text-xs text-muted-foreground bg-muted/30">
                    Płatności obsługiwane przez Lemon Squeezy. Możesz anulować w każdej chwili.
                </div>
            </DialogContent>
        </Dialog>
    );
}
